// pages/Ledger.js - Fixed with Correct Transaction Processing

import { useEffect, useState } from "react";
import { database } from "../firebase/firebase";
import { ref, onValue, get } from "firebase/database";
import { useNavigate } from "react-router-dom";
import "../styles/main.css";

export default function Ledger() {
  const navigate = useNavigate();
  
  // State for account search
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [filteredAccounts, setFilteredAccounts] = useState([]);
  
  // State for transactions
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // State for financial year
  const [financialYear, setFinancialYear] = useState("");
  const [availableYears, setAvailableYears] = useState([]);
  const [settings, setSettings] = useState({ financialYear: "2025-26" });
  
  // State for company details
  const [companyDetails, setCompanyDetails] = useState({
    name: "",
    address: "",
    phone: "",
    email: "",
    gstin: "",
    state: "",
    stateCode: "",
    terms: "Thank you for your business!"
  });
  
  // State for date filter
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  
  // State for filtered transactions
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  
  // Calculate balances
  const [openingBalance, setOpeningBalance] = useState(0);
  const [closingBalance, setClosingBalance] = useState(0);
  const [totalDebit, setTotalDebit] = useState(0);
  const [totalCredit, setTotalCredit] = useState(0);
  const [amountPayable, setAmountPayable] = useState(0);
  const [amountReceivable, setAmountReceivable] = useState(0);

  // Load settings
  useEffect(() => {
    const settingsRef = ref(database, "settings");
    onValue(settingsRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        setSettings(prev => ({
          ...prev,
          financialYear: data.financialYear || "2025-26"
        }));
        setFinancialYear(data.financialYear || "2025-26");
      }
    });
  }, []);

  // Load company details
  useEffect(() => {
    const companyRef = ref(database, "company");
    onValue(companyRef, (snapshot) => {
      if (snapshot.exists()) {
        setCompanyDetails(snapshot.val());
      }
    });
  }, []);

  // Get available financial years
  useEffect(() => {
    const fetchFinancialYears = async () => {
      const financialYearsRef = ref(database, "financialYears");
      const snapshot = await get(financialYearsRef);
      
      if (snapshot.exists()) {
        const years = Object.keys(snapshot.val());
        setAvailableYears(years.sort().reverse());
      } else {
        setAvailableYears([settings.financialYear]);
      }
    };
    
    fetchFinancialYears();
  }, [settings.financialYear]);

  // Load all accounts (customers and bank accounts)
  useEffect(() => {
    const customersRef = ref(database, "customers");
    onValue(customersRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        const accountList = Object.entries(data).map(([id, account]) => ({
          id,
          name: account.name,
          mobile: account.mobile,
          gstin: account.gstin || account.gst,
          address: account.address,
          state: account.state,
          stateCode: account.stateCode,
          openingBalance: account.openingBalance || 0,
          openingBalanceDate: account.openingBalanceDate || "",
          creditLimit: account.creditLimit || 0,
          paymentTerms: account.paymentTerms || "",
          masterType: account.masterType || "sundryDebtor",
          bankName: account.bankName || "",
          accountNumber: account.accountNumber || "",
          accountHolderName: account.accountHolderName || "",
          ifscCode: account.ifscCode || ""
        }));
        setAccounts(accountList);
      }
    });
  }, []);

  // Filter accounts based on search
  useEffect(() => {
    if (searchTerm.trim() === "") {
      setFilteredAccounts([]);
      return;
    }
    
    const filtered = accounts.filter(account => 
      account.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      account.mobile?.includes(searchTerm) ||
      account.bankName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      account.accountNumber?.includes(searchTerm)
    );
    setFilteredAccounts(filtered);
  }, [searchTerm, accounts]);

  // Load transactions when account or financial year changes
  useEffect(() => {
    if (selectedAccount && financialYear) {
      loadAccountTransactions();
    }
  }, [selectedAccount, financialYear]);

  // Filter transactions by date
  useEffect(() => {
    if (transactions.length === 0) return;
    
    let filtered = [...transactions];
    
    if (startDate) {
      filtered = filtered.filter(t => t.date >= startDate);
    }
    if (endDate) {
      filtered = filtered.filter(t => t.date <= endDate);
    }
    
    // Sort by date (oldest first)
    filtered.sort((a, b) => new Date(a.date) - new Date(b.date));
    
    setFilteredTransactions(filtered);
    calculateBalances(filtered);
  }, [transactions, startDate, endDate]);

  // Load transactions based on account type
  const loadAccountTransactions = async () => {
    setLoading(true);
    
    try {
      const allTransactions = [];
      
      if (selectedAccount.masterType === "bankAccount") {
        // Load bank account transactions from payments
        await loadBankTransactions(allTransactions);
      } else {
        // Load customer/supplier transactions
        await loadCustomerTransactions(allTransactions);
      }
      
      // Sort by date
      allTransactions.sort((a, b) => new Date(a.date) - new Date(b.date));
      
      setTransactions(allTransactions);
      setOpeningBalance(selectedAccount.openingBalance || 0);
      
    } catch (error) {
      console.error("Error loading transactions:", error);
    } finally {
      setLoading(false);
    }
  };

  // Load bank account transactions
  const loadBankTransactions = async (allTransactions) => {
    const paymentsRef = ref(database, `financialYears/${financialYear}/payments`);
    const paymentsSnapshot = await get(paymentsRef);
    
    if (paymentsSnapshot.exists()) {
      const payments = paymentsSnapshot.val();
      Object.entries(payments).forEach(([id, payment]) => {
        if (payment.bankAccountId === selectedAccount.id || payment.bankName === selectedAccount.bankName) {
          if (payment.paymentType === "received") {
            // Money received into bank account - DEBIT (Deposit increases balance)
            allTransactions.push({
              id,
              type: "bank",
              typeLabel: "Deposit",
              date: payment.paymentDate,
              voucherNo: payment.paymentNo,
              description: `Received from ${payment.partyName} via ${payment.paymentMode.toUpperCase()}`,
              debit: payment.amount,
              credit: 0,
              balance: 0
            });
          } else {
            // Money paid from bank account - CREDIT (Withdrawal decreases balance)
            allTransactions.push({
              id,
              type: "bank",
              typeLabel: "Withdrawal",
              date: payment.paymentDate,
              voucherNo: payment.paymentNo,
              description: `Paid to ${payment.partyName} via ${payment.paymentMode.toUpperCase()}`,
              debit: 0,
              credit: payment.amount,
              balance: 0
            });
          }
        }
      });
    }
  };

  // Load customer/supplier transactions
  const loadCustomerTransactions = async (allTransactions) => {
    // Load Sales Invoices - CREDIT (Customer owes money - increases receivable)
    const billsRef = ref(database, `financialYears/${financialYear}/bills`);
    const billsSnapshot = await get(billsRef);
    
    if (billsSnapshot.exists()) {
      const bills = billsSnapshot.val();
      Object.entries(bills).forEach(([id, bill]) => {
        if (bill.customerId === selectedAccount.id || bill.customerName === selectedAccount.name) {
          allTransactions.push({
            id,
            type: "invoice",
            typeLabel: "Sales Invoice",
            date: bill.date,
            voucherNo: bill.invoiceNo,
            description: `Sale of ${bill.items?.length || 0} items`,
            debit: 0,
            credit: bill.grandTotal || 0,
            balance: 0
          });
        }
      });
    }
    
    // Load Cash Bills - CREDIT (Customer owes money)
    const rawBillsRef = ref(database, `financialYears/${financialYear}/rawBills`);
    const rawBillsSnapshot = await get(rawBillsRef);
    
    if (rawBillsSnapshot.exists()) {
      const rawBills = rawBillsSnapshot.val();
      Object.entries(rawBills).forEach(([id, bill]) => {
        if (bill.customerId === selectedAccount.id || bill.customerName === selectedAccount.name) {
          allTransactions.push({
            id,
            type: "cashBill",
            typeLabel: "Cash Bill",
            date: bill.date,
            voucherNo: bill.billNo,
            description: `Cash sale of ${bill.items?.length || 0} items`,
            debit: 0,
            credit: bill.roundedTotal || bill.grandTotal || 0,
            balance: 0
          });
        }
      });
    }
    
    // Load Purchases - DEBIT (We owe money to supplier - increases payable)
    const purchasesRef = ref(database, `financialYears/${financialYear}/purchases`);
    const purchasesSnapshot = await get(purchasesRef);
    
    if (purchasesSnapshot.exists()) {
      const purchases = purchasesSnapshot.val();
      Object.entries(purchases).forEach(([id, purchase]) => {
        if (purchase.supplierId === selectedAccount.id || purchase.supplierName === selectedAccount.name) {
          allTransactions.push({
            id,
            type: "purchase",
            typeLabel: "Purchase",
            date: purchase.date,
            voucherNo: purchase.purchaseNo,
            description: `Purchase of ${purchase.items?.length || 0} items`,
            debit: purchase.grandTotal || 0,
            credit: 0,
            balance: 0
          });
        }
      });
    }
    
    // Load Payments - DEBIT for received payments, CREDIT for paid payments
    const paymentsRef = ref(database, `financialYears/${financialYear}/payments`);
    const paymentsSnapshot = await get(paymentsRef);
    
    if (paymentsSnapshot.exists()) {
      const payments = paymentsSnapshot.val();
      Object.entries(payments).forEach(([id, payment]) => {
        if (payment.partyId === selectedAccount.id || payment.partyName === selectedAccount.name) {
          if (payment.paymentType === "received") {
            // Payment received from customer - DEBIT (Reduces receivable)
            // This should DECREASE the balance
            allTransactions.push({
              id,
              type: "payment",
              typeLabel: "Payment Received",
              date: payment.paymentDate,
              voucherNo: payment.paymentNo,
              description: `Payment received via ${payment.paymentMode.toUpperCase()}${payment.referenceNo ? ` (Ref: ${payment.referenceNo})` : ''}`,
              debit: payment.amount,  // Debit decreases receivable
              credit: 0,
              balance: 0
            });
          } else {
            // Payment paid to supplier - CREDIT (Reduces payable)
            allTransactions.push({
              id,
              type: "payment",
              typeLabel: "Payment Paid",
              date: payment.paymentDate,
              voucherNo: payment.paymentNo,
              description: `Payment made via ${payment.paymentMode.toUpperCase()}${payment.referenceNo ? ` (Ref: ${payment.referenceNo})` : ''}`,
              debit: 0,
              credit: payment.amount,
              balance: 0
            });
          }
        }
      });
    }
  };

  // Calculate running balance
  // For customer: Positive balance means they owe us (Receivable)
  // For customer: Negative balance means we owe them (Payable)
  const calculateBalances = (transactionsList) => {
    // Start with opening balance
    let runningBalance = openingBalance;
    
    const updatedTransactions = transactionsList.map(transaction => {
      // For customer accounts:
      // Debit (Payment Received) decreases the balance (customer pays us)
      // Credit (Sales Invoice) increases the balance (customer owes us)
      runningBalance = runningBalance + transaction.debit - transaction.credit;
      
      return {
        ...transaction,
        balance: runningBalance
      };
    });
    
    const totalDebitAmount = updatedTransactions.reduce((sum, t) => sum + (t.debit || 0), 0);
    const totalCreditAmount = updatedTransactions.reduce((sum, t) => sum + (t.credit || 0), 0);
    
    const finalBalance = openingBalance + totalDebitAmount - totalCreditAmount;
    
    setTotalDebit(totalDebitAmount);
    setTotalCredit(totalCreditAmount);
    setClosingBalance(finalBalance);
    
    if (finalBalance > 0) {
      setAmountReceivable(finalBalance);
      setAmountPayable(0);
    } else if (finalBalance < 0) {
      setAmountReceivable(0);
      setAmountPayable(Math.abs(finalBalance));
    } else {
      setAmountReceivable(0);
      setAmountPayable(0);
    }
    
    setFilteredTransactions(updatedTransactions);
  };

  // Handle account selection
  const selectAccount = (account) => {
    setSelectedAccount(account);
    setSearchTerm(account.name);
    setShowDropdown(false);
    setStartDate("");
    setEndDate("");
    setOpeningBalance(account.openingBalance || 0);
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric"
    }).replace(/\//g, '-');
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  // Get transaction description with icon
  const getTransactionDescription = (transaction) => {
    if (transaction.type === "payment") {
      if (transaction.debit > 0) {
        return `💰 ${transaction.description}`;
      } else {
        return `💸 ${transaction.description}`;
      }
    } else if (transaction.type === "bank") {
      if (transaction.debit > 0) {
        return `🏦 Deposit: ${transaction.description}`;
      } else {
        return `🏧 Withdrawal: ${transaction.description}`;
      }
    } else if (transaction.type === "invoice" || transaction.type === "cashBill") {
      return `🧾 ${transaction.description}`;
    } else if (transaction.type === "purchase") {
      return `📦 ${transaction.description}`;
    }
    return transaction.description;
  };

  // Handle print
  const handlePrint = () => {
    const transactionRows = filteredTransactions.map(t => {
      const description = getTransactionDescription(t);
      return `
        <tr>
          <td>${formatDate(t.date)}</td>
          <td>${t.voucherNo || "-"}</td>
          <td>${description}</td>
          <td class="number">${t.debit > 0 ? formatCurrency(t.debit) : "-"}</td>
          <td class="number">${t.credit > 0 ? formatCurrency(t.credit) : "-"}</td>
          <td class="number">${formatCurrency(t.balance)}</td>
         </tr>
      `;
    }).join("");
    
    const printWindow = window.open("", "_blank");
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Ledger - ${selectedAccount?.name}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: monospace; font-size: 12px; padding: 20px; }
          .header { text-align: center; margin-bottom: 20px; border-bottom: 2px solid #000; }
          .account-info { margin: 15px 0; padding: 10px; border: 1px solid #000; background: #f9f9f9; }
          table { width: 100%; border-collapse: collapse; margin-top: 15px; }
          th, td { border: 1px solid #000; padding: 6px; text-align: left; }
          .number { text-align: right; }
          .summary { margin-top: 20px; padding: 10px; border: 1px solid #000; background: #f9f9f9; }
          .footer { margin-top: 30px; text-align: center; font-size: 10px; }
          @media print { body { padding: 0; } }
        </style>
      </head>
      <body>
        <div class="header">
          <h2>${companyDetails.name || "JAUHARI BOTTLERS"}</h2>
          <div>${companyDetails.address || ""}</div>
        </div>
        <div class="account-info">
          <strong>Account:</strong> ${selectedAccount?.name}<br>
          <strong>Type:</strong> ${selectedAccount?.masterType === "bankAccount" ? "Bank Account" : "Customer/Supplier"}<br>
          <strong>Financial Year:</strong> ${financialYear}<br>
          <strong>Opening Balance:</strong> ${formatCurrency(openingBalance)} ${openingBalance > 0 ? "(Receivable)" : openingBalance < 0 ? "(Payable)" : ""}
        </div>
        <table>
          <thead>
            <tr><th>Date</th><th>Vch No.</th><th>Particulars</th><th class="number">Debit (Dr)</th><th class="number">Credit (Cr)</th><th class="number">Balance</th></tr>
          </thead>
          <tbody>
            <tr style="background:#f5f5f5;">
              <td colspan="3"><strong>Opening Balance</strong></td>
              <td class="number">${openingBalance > 0 ? formatCurrency(openingBalance) : "-"}</td>
              <td class="number">${openingBalance < 0 ? formatCurrency(Math.abs(openingBalance)) : "-"}</td>
              <td class="number">${formatCurrency(openingBalance)}</td>
            </tr>
            ${transactionRows}
          </tbody>
        </table>
        <div class="summary">
          <strong>Total Debit (Dr):</strong> ${formatCurrency(totalDebit)}<br>
          <strong>Total Credit (Cr):</strong> ${formatCurrency(totalCredit)}<br>
          <strong>Closing Balance:</strong> ${formatCurrency(closingBalance)} ${closingBalance > 0 ? "(Receivable)" : closingBalance < 0 ? "(Payable)" : ""}
        </div>
        <div class="footer">${companyDetails.terms || "Thank you for your business!"}</div>
      </body>
      </html>
    `);
    printWindow.document.close();
  };

  return (
    <div className="ledger-page">
      <div className="page-header">
        <h2>📒 Account Ledger</h2>
        <div className="header-actions">
          <button onClick={() => navigate("/")} className="back-btn">🏠 Home</button>
        </div>
      </div>
      
      <div className="financial-year-selector">
        <label>Financial Year:</label>
        <select value={financialYear} onChange={(e) => setFinancialYear(e.target.value)} className="year-select">
          {availableYears.map(year => (<option key={year} value={year}>{year}</option>))}
        </select>
      </div>
      
      <div className="account-search-section">
        <div className="search-container">
          <input 
            type="text" 
            className="account-search-input" 
            placeholder="🔍 Search customer, supplier, or bank account..." 
            value={searchTerm} 
            onChange={(e) => { 
              setSearchTerm(e.target.value); 
              setShowDropdown(true); 
              if (!e.target.value) setSelectedAccount(null); 
            }} 
            onFocus={() => setShowDropdown(true)} 
          />
          
          {showDropdown && filteredAccounts.length > 0 && (
            <div className="account-dropdown">
              {filteredAccounts.map(account => (
                <div key={account.id} className="account-option" onClick={() => selectAccount(account)}>
                  <div className="account-name">
                    <strong>{account.name}</strong>
                    {account.masterType === "bankAccount" && <span className="bank-badge">🏦 Bank</span>}
                    {account.masterType === "sundryCreditor" && <span className="supplier-badge">🏭 Supplier</span>}
                    {account.masterType === "sundryDebtor" && <span className="customer-badge">👤 Customer</span>}
                  </div>
                  <div className="account-detail">
                    {account.mobile && <span>📱 {account.mobile}</span>}
                    {account.bankName && <span> | 🏦 {account.bankName}</span>}
                    <span className="opening-balance"> | Opening: {formatCurrency(Math.abs(account.openingBalance))} {account.openingBalance > 0 ? "Dr" : account.openingBalance < 0 ? "Cr" : ""}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      
      {selectedAccount && (
        <>
          <div className="selected-account-card">
            <div className="account-header">
              <div>
                <h3>{selectedAccount.name}</h3>
                <div className="account-details">
                  {selectedAccount.mobile && <span>📱 {selectedAccount.mobile}</span>}
                  {selectedAccount.gstin && <span> | 🆔 GST: {selectedAccount.gstin}</span>}
                  {selectedAccount.address && <span> | 📍 {selectedAccount.address}</span>}
                  {selectedAccount.bankName && <span> | 🏦 {selectedAccount.bankName}</span>}
                  <div className="opening-balance-display">
                    <strong>Opening Balance:</strong> 
                    <span className={openingBalance > 0 ? "positive" : openingBalance < 0 ? "negative" : "zero"}>
                      {formatCurrency(Math.abs(openingBalance))} {openingBalance > 0 ? "(Receivable - Customer owes us)" : openingBalance < 0 ? "(Payable - We owe)" : "(Zero)"}
                    </span>
                  </div>
                </div>
              </div>
              <button className="change-btn" onClick={() => { setSelectedAccount(null); setSearchTerm(""); setTransactions([]); }}>Change</button>
            </div>
          </div>
          
          <div className="date-filters">
            <div className="filter-group"><label>From Date:</label><input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} /></div>
            <div className="filter-group"><label>To Date:</label><input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} /></div>
            <button className="clear-filters" onClick={() => { setStartDate(""); setEndDate(""); }}>Clear</button>
            <button className="print-ledger-btn" onClick={handlePrint} disabled={filteredTransactions.length === 0}>🖨️ Print Ledger</button>
          </div>
          
          {filteredTransactions.length > 0 && (
            <div className={`amount-status ${closingBalance > 0 ? 'receivable' : closingBalance < 0 ? 'payable' : 'zero'}`}>
              <div className="status-icon">{closingBalance > 0 ? "💰" : closingBalance < 0 ? "💸" : "⚖️"}</div>
              <div className="status-info">
                <div className="status-label">{closingBalance > 0 ? "BALANCE RECEIVABLE" : closingBalance < 0 ? "BALANCE PAYABLE" : "ZERO BALANCE"}</div>
                <div className="status-amount">₹ {formatCurrency(Math.abs(closingBalance))}</div>
                <div className="status-subtext">
                  {closingBalance > 0 ? `${selectedAccount.name} owes this amount` : 
                   closingBalance < 0 ? `We owe this amount to ${selectedAccount.name}` : 
                   "Account is fully settled"}
                </div>
              </div>
            </div>
          )}
          
          <div className="transaction-table-container">
            <div className="table-header">
              <h3>Transaction Details</h3>
              <span className="record-count">{filteredTransactions.length} records</span>
            </div>
            
            {loading ? (
              <div className="loading-state"><div className="spinner"></div><p>Loading transactions...</p></div>
            ) : filteredTransactions.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">📭</div>
                <p>No transactions found for this account</p>
                <p className="hint">Create invoices, payments, or purchases to see transactions here</p>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="ledger-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Vch No.</th>
                      <th>Particulars</th>
                      <th className="number">Debit (Dr)</th>
                      <th className="number">Credit (Cr)</th>
                      <th className="number">Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="opening-row">
                      <td colSpan="3"><strong>Opening Balance</strong></td>
                      <td className="number debit-amount">{openingBalance > 0 ? formatCurrency(openingBalance) : "-"}</td>
                      <td className="number credit-amount">{openingBalance < 0 ? formatCurrency(Math.abs(openingBalance)) : "-"}</td>
                      <td className="number balance-amount">{formatCurrency(openingBalance)}</td>
                    </tr>
                    
                    {filteredTransactions.map((transaction, index) => (
                      <tr 
                        key={`${transaction.id}-${index}`} 
                        className="transaction-row"
                        onClick={() => {
                          if (transaction.type === "invoice") navigate(`/invoice/${transaction.id}`);
                          else if (transaction.type === "cashBill") navigate(`/raw-bill/${transaction.id}`);
                          else if (transaction.type === "purchase") navigate(`/purchase/${transaction.id}`);
                          else if (transaction.type === "payment") navigate(`/payment/edit/${transaction.id}`);
                        }}
                      >
                        <td>{formatDate(transaction.date)}</td>
                        <td>{transaction.voucherNo || "-"}</td>
                        <td className={transaction.type === "payment" ? (transaction.debit > 0 ? "payment-debit" : "payment-credit") : ""}>
                          {getTransactionDescription(transaction)}
                        </td>
                        <td className="number debit-amount">{transaction.debit > 0 ? formatCurrency(transaction.debit) : "-"}</td>
                        <td className="number credit-amount">{transaction.credit > 0 ? formatCurrency(transaction.credit) : "-"}</td>
                        <td className="number balance-amount">{formatCurrency(transaction.balance)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="total-row">
                      <td colSpan="3"><strong>Totals</strong></td>
                      <td className="number"><strong>{formatCurrency(totalDebit)}</strong></td>
                      <td className="number"><strong>{formatCurrency(totalCredit)}</strong></td>
                      <td className="number"><strong>{formatCurrency(closingBalance)}</strong></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
        </>
      )}
      
      {/* ================= BOTTOM NAV ================= */}
      <div className="bottom-nav">
        <div onClick={() => navigate("/")} className="nav-item active">
          🏠
          <p>Home</p>
        </div>
        <div onClick={() => navigate("/items")} className="nav-item">
          📦
          <p>Items</p>
        </div>
        <div onClick={() => navigate("/customers")} className="nav-item">
          👥
          <p>Masters</p>
        </div>
        <div onClick={() => navigate("/invoicelist")} className="nav-item">
          🧾
          <p>Invoices</p>
        </div>
      </div>
      <style>{`
        .ledger-page { min-height: 100vh; background: #f5f5f5; padding-bottom: 70px; }
        .page-header { display: flex; align-items: center; justify-content: space-between; padding: 15px 20px; background: white; border-bottom: 1px solid #eee; position: sticky; top: 0; z-index: 100; }
        .page-header h2 { margin: 0; font-size: 20px; color: #333; }
        .back-btn { background: #f0f0f0; color: #333; border: none; padding: 8px 16px; border-radius: 20px; font-size: 14px; cursor: pointer; }
        .financial-year-selector { background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; padding: 12px 20px; margin: 10px 20px; border-radius: 8px; display: flex; align-items: center; gap: 15px; font-size: 14px; }
        .year-select { padding: 6px 12px; border-radius: 6px; border: none; background: white; font-weight: bold; cursor: pointer; }
        .account-search-section { margin: 0 20px 20px; }
        .search-container { position: relative; }
        .account-search-input { width: 100%; padding: 14px 16px; border: 1px solid #ddd; border-radius: 12px; font-size: 16px; background: white; box-shadow: 0 2px 8px rgba(0,0,0,0.05); }
        .account-dropdown { position: absolute; top: 100%; left: 0; right: 0; max-height: 350px; overflow-y: auto; background: white; border: 1px solid #ddd; border-radius: 12px; margin-top: 5px; z-index: 1000; box-shadow: 0 4px 12px rgba(0,0,0,0.15); }
        .account-option { padding: 12px 16px; border-bottom: 1px solid #f0f0f0; cursor: pointer; transition: background 0.2s; }
        .account-option:hover { background: #f5f5f5; }
        .account-name { font-weight: bold; font-size: 14px; color: #333; margin-bottom: 4px; }
        .account-detail { font-size: 11px; color: #666; }
        .bank-badge { background: #5a60ea20; color: #5a60ea; padding: 2px 8px; border-radius: 12px; margin-left: 8px; font-size: 10px; }
        .supplier-badge { background: #ff980020; color: #f57c00; padding: 2px 8px; border-radius: 12px; margin-left: 8px; font-size: 10px; }
        .customer-badge { background: #28a74520; color: #28a745; padding: 2px 8px; border-radius: 12px; margin-left: 8px; font-size: 10px; }
        .opening-balance { font-size: 10px; }
        .selected-account-card { background: white; margin: 0 20px 20px; padding: 15px; border-radius: 12px; box-shadow: 0 2px 8px rgba(0,0,0,0.05); border-left: 4px solid #5a60ea; }
        .account-header { display: flex; justify-content: space-between; align-items: flex-start; }
        .account-header h3 { margin: 0 0 8px 0; font-size: 16px; color: #333; }
        .account-details { font-size: 12px; color: #666; line-height: 1.5; }
        .opening-balance-display { margin-top: 8px; padding-top: 5px; font-size: 12px; border-top: 1px solid #eee; }
        .opening-balance-display .positive { color: #28a745; font-weight: bold; }
        .opening-balance-display .negative { color: #dc3545; font-weight: bold; }
        .opening-balance-display .zero { color: #666; }
        .change-btn { background: #5a60ea; color: white; border: none; padding: 6px 15px; border-radius: 20px; font-size: 12px; cursor: pointer; }
        .date-filters { display: flex; flex-wrap: wrap; gap: 10px; margin: 0 20px 20px; padding: 15px; background: white; border-radius: 12px; align-items: flex-end; }
        .filter-group { display: flex; flex-direction: column; gap: 5px; flex: 1; min-width: 120px; }
        .filter-group label { font-size: 12px; color: #666; font-weight: 500; }
        .filter-group input { padding: 10px; border: 1px solid #ddd; border-radius: 8px; font-size: 14px; }
        .clear-filters, .print-ledger-btn { padding: 10px 20px; border: none; border-radius: 8px; font-size: 13px; cursor: pointer; }
        .clear-filters { background: #f0f0f0; color: #666; }
        .print-ledger-btn { background: #28a745; color: white; }
        .print-ledger-btn:disabled { background: #ccc; cursor: not-allowed; }
        .amount-status { margin: 0 20px 20px; padding: 20px; border-radius: 12px; display: flex; align-items: center; gap: 15px; box-shadow: 0 2px 8px rgba(0,0,0,0.1); }
        .amount-status.receivable { background: linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%); border-left: 4px solid #28a745; }
        .amount-status.payable { background: linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%); border-left: 4px solid #dc3545; }
        .amount-status.zero { background: #f5f5f5; border-left: 4px solid #999; }
        .status-icon { font-size: 48px; }
        .status-info { flex: 1; }
        .status-label { font-size: 12px; font-weight: 500; letter-spacing: 1px; margin-bottom: 5px; }
        .status-amount { font-size: 28px; font-weight: bold; }
        .amount-status.receivable .status-amount { color: #28a745; }
        .amount-status.payable .status-amount { color: #dc3545; }
        .status-subtext { font-size: 11px; margin-top: 5px; opacity: 0.8; }
        .transaction-table-container { margin: 0 20px 20px; background: white; border-radius: 12px; overflow: hidden; box-shadow: 0 2px 8px rgba(0,0,0,0.05); }
        .table-header { display: flex; justify-content: space-between; align-items: center; padding: 15px 20px; border-bottom: 1px solid #eee; }
        .table-header h3 { margin: 0; font-size: 16px; }
        .record-count { font-size: 12px; color: #666; }
        .table-responsive { overflow-x: auto; }
        .ledger-table { width: 100%; border-collapse: collapse; }
        .ledger-table th, .ledger-table td { padding: 10px 12px; text-align: left; border-bottom: 1px solid #f0f0f0; font-size: 12px; }
        .ledger-table th { background: #fafafa; font-weight: 600; color: #333; }
        .ledger-table .number { text-align: right; }
        .opening-row { background: #f9f9f9; font-weight: 500; }
        .total-row { background: #f5f5f5; font-weight: bold; }
        .transaction-row { cursor: pointer; transition: background 0.2s; }
        .transaction-row:hover { background: #f5f5f5; }
        .debit-amount { color: #28a745; font-weight: 600; }
        .credit-amount { color: #dc3545; font-weight: 600; }
        .balance-amount { font-weight: 500; color: #5a60ea; }
        .payment-debit { color: #28a745; }
        .payment-credit { color: #dc3545; }
        .loading-state, .empty-state { text-align: center; padding: 40px 20px; }
        .empty-icon { font-size: 48px; margin-bottom: 15px; }
        .empty-state .hint { font-size: 12px; color: #999; margin-top: 5px; }
        .spinner { display: inline-block; width: 30px; height: 30px; border: 3px solid #f0f0f0; border-top-color: #5a60ea; border-radius: 50%; animation: spin 1s linear infinite; margin-bottom: 10px; }
        @keyframes spin { to { transform: rotate(360deg); } }
        .bottom-nav { position: fixed; bottom: 0; left: 0; right: 0; background: white; display: flex; justify-content: space-around; padding: 8px 0; box-shadow: 0 -2px 10px rgba(0,0,0,0.05); z-index: 100; }
        .bottom-nav div { text-align: center; cursor: pointer; color: #999; font-size: 20px; }
        .bottom-nav p { margin: 2px 0 0; font-size: 11px; }
      `}</style>
    </div>
  );
}